import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  FC,
} from 'react';
import { fetchUserProfileInfo } from 'src/utils/company.utils';
import Cookies from 'js-cookie';
import { dataStorageKeywords, errorMessages } from 'src/utils/properties.utils';

interface UserProfileData {
  firstName: string;
  lastName: string;
  fullName: string;
  countryCode: string;
  phoneNumber: string;
  avatar: string;
  avatarUrl: string;
}

interface UserProfileContextType {
  userProfile: UserProfileData | null;
  isLoading: boolean;
  error: string | null;
  refreshUserProfile: () => Promise<void>;
  updateUserProfile: (data: Partial<UserProfileData>) => void;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(
  undefined
);

interface UserProfileProviderProps {
  children: ReactNode;
}

export const UserProfileProvider: FC<UserProfileProviderProps> = ({
  children,
}) => {
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (Cookies.get(dataStorageKeywords.ACCESS_TOKEN)) {
      refreshUserProfile();
    }
  }, []);

  const refreshUserProfile = async () => {
    const token = Cookies.get(dataStorageKeywords.ACCESS_TOKEN);

    if (!token) {
      setUserProfile(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchUserProfileInfo();
      const fullName = `${data.firstName} ${data.lastName}`;

      const newProfile = {
        firstName: data.firstName,
        lastName: data.lastName,
        fullName,
        countryCode: data.countryCode,
        phoneNumber: data.phoneNumber,
        avatar: data.avatar || '',
        avatarUrl: data.avatarUrl,
      };

      setUserProfile(newProfile);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch user profile');
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserProfile = (data: Partial<UserProfileData>) => {
    setUserProfile((prev) => (prev ? { ...prev, ...data } : null));
  };

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        isLoading,
        error,
        refreshUserProfile,
        updateUserProfile,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => useContext(UserProfileContext)!;
